import { FormEvent, useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

import type { StatementSection } from "../../services/financialService";

type StatementKind = StatementSection["statement"];

interface Props {
  sections?: StatementSection[];
  onAddSection: (data: { name: string; statement: StatementKind; account_types: string[] }) => void;
  onRenameSection: (data: { id: number; name: string }) => void;
  onDeleteSection: (id: number) => void;
  onAddCategory: (data: { section: number; name: string }) => void;
  onRenameCategory: (data: { id: number; name: string }) => void;
  onDeleteCategory: (id: number) => void;
  onReorder: (data: { sections?: number[]; categories?: Record<number, number[]> }) => void;
}

const STATEMENTS = [
  { value: "financial_position", label: "Statement of financial position", types: ["asset", "liability", "equity"] },
  { value: "income_expenditure", label: "Income and expenditure", types: ["income", "expense"] },
] as const;

/** Move `id` one place up or down, returning the new order. */
const moved = (ids: number[], id: number, direction: -1 | 1) => {
  const from = ids.indexOf(id);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= ids.length) return null;
  const next = [...ids];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
};

export default function StatementTemplateEditor({
  sections, onAddSection, onRenameSection, onDeleteSection,
  onAddCategory, onRenameCategory, onDeleteCategory, onReorder,
}: Props) {
  const [addingTo, setAddingTo] = useState<number | null>(null);
  const [categoryName, setCategoryName] = useState("");
  const [newStatement, setNewStatement] = useState<StatementKind>("financial_position");

  if (!sections) {
    return <div className="card"><div className="card-content text-sm text-gray-500">Loading template…</div></div>;
  }

  const submitSection = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") || "").trim();
    if (!name) return;
    onAddSection({
      name,
      statement: newStatement,
      account_types: form.getAll("account_types").map(String),
    });
    event.currentTarget.reset();
  };

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="border-b border-secondary-200 px-6 py-4">
          <h3 className="font-semibold text-gray-900">Statement template</h3>
          <p className="mt-1 text-sm text-gray-500">
            The headings your statements are built from, in the order they read. Renaming, reordering
            or adding here changes how every statement is presented — it never changes the underlying
            figures.
          </p>
        </div>
      </div>

      {STATEMENTS.map(({ value, label }) => {
        const inStatement = sections.filter((section) => section.statement === value);
        const order = inStatement.map((section) => section.id);
        return (
          <div key={value} className="card">
            <div className="border-b border-secondary-200 px-6 py-3">
              <h4 className="font-medium text-gray-900">{label}</h4>
            </div>
            <div className="divide-y">
              {inStatement.map((section) => {
                const categoryOrder = section.categories.map((category) => category.id);
                return (
                  <div key={section.id} className="px-6 py-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex flex-col">
                        <button
                          type="button"
                          aria-label={`Move ${section.name} up`}
                          className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                          disabled={order[0] === section.id}
                          onClick={() => {
                            const next = moved(order, section.id, -1);
                            if (next) onReorder({ sections: next });
                          }}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${section.name} down`}
                          className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                          disabled={order[order.length - 1] === section.id}
                          onClick={() => {
                            const next = moved(order, section.id, 1);
                            if (next) onReorder({ sections: next });
                          }}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </div>
                      <input
                        className="input flex-1 font-medium"
                        defaultValue={section.name}
                        aria-label="Section name"
                        onBlur={(event) => {
                          const name = event.target.value.trim();
                          if (name && name !== section.name) onRenameSection({ id: section.id, name });
                        }}
                      />
                      <span className="rounded bg-secondary-100 px-2 py-1 text-xs capitalize text-secondary-700">
                        {section.permitted_account_types.join(", ")}
                      </span>
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-800"
                        aria-label={`Delete ${section.name}`}
                        onClick={() => onDeleteSection(section.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="mt-3 space-y-2 pl-8">
                      {section.categories.map((category) => (
                        <div key={category.id} className="flex flex-wrap items-center gap-2">
                          <div className="flex flex-col">
                            <button
                              type="button"
                              aria-label={`Move ${category.name} up`}
                              className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                              disabled={categoryOrder[0] === category.id}
                              onClick={() => {
                                const next = moved(categoryOrder, category.id, -1);
                                if (next) onReorder({ categories: { [section.id]: next } });
                              }}
                            >
                              <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Move ${category.name} down`}
                              className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                              disabled={categoryOrder[categoryOrder.length - 1] === category.id}
                              onClick={() => {
                                const next = moved(categoryOrder, category.id, 1);
                                if (next) onReorder({ categories: { [section.id]: next } });
                              }}
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <input
                            className="input flex-1 text-sm"
                            defaultValue={category.name}
                            aria-label="Category name"
                            onBlur={(event) => {
                              const name = event.target.value.trim();
                              if (name && name !== category.name) onRenameCategory({ id: category.id, name });
                            }}
                          />
                          <span className="whitespace-nowrap text-xs text-gray-500">
                            {category.account_count} account{category.account_count === 1 ? "" : "s"}
                          </span>
                          <button
                            type="button"
                            className="text-red-600 hover:text-red-800 disabled:opacity-30"
                            aria-label={`Delete ${category.name}`}
                            disabled={category.account_count > 0}
                            title={category.account_count > 0
                              ? "Move its accounts elsewhere before deleting this heading"
                              : undefined}
                            onClick={() => onDeleteCategory(category.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}

                      {addingTo === section.id ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            autoFocus
                            className="input flex-1 text-sm"
                            placeholder="New heading name"
                            value={categoryName}
                            onChange={(event) => setCategoryName(event.target.value)}
                          />
                          <button
                            className="btn btn-primary px-3 py-1.5 text-sm"
                            disabled={!categoryName.trim()}
                            onClick={() => {
                              onAddCategory({ section: section.id, name: categoryName.trim() });
                              setCategoryName("");
                              setAddingTo(null);
                            }}
                          >
                            Add
                          </button>
                          <button
                            className="px-2 py-1.5 text-sm text-gray-500"
                            onClick={() => { setAddingTo(null); setCategoryName(""); }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="text-sm text-primary-700 hover:underline"
                          onClick={() => { setAddingTo(section.id); setCategoryName(""); }}
                        >
                          + Add a heading under {section.name}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {!inStatement.length && (
                <p className="px-6 py-4 text-sm text-gray-500">No sections on this statement yet.</p>
              )}
            </div>
          </div>
        );
      })}

      <form onSubmit={submitSection} className="card">
        <div className="border-b border-secondary-200 px-6 py-3">
          <h4 className="font-medium text-gray-900">Add a section</h4>
        </div>
        <div className="card-content grid gap-3 md:grid-cols-3">
          <input required name="name" className="input" placeholder="Section name" />
          <select
            className="input"
            value={newStatement}
            onChange={(event) => setNewStatement(event.target.value as StatementKind)}
          >
            {STATEMENTS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <button className="btn btn-primary">
            <Plus className="mr-2 h-4 w-4" />
            Add section
          </button>
          <fieldset className="md:col-span-3">
            <legend className="text-xs font-medium text-gray-500">
              Which accounts may sit in it — leave all unticked to allow any this statement carries
            </legend>
            <div className="mt-2 flex flex-wrap gap-4 text-sm">
              {STATEMENTS.find((item) => item.value === newStatement)?.types.map((type) => (
                <label key={type} className="flex items-center gap-2 capitalize">
                  <input type="checkbox" name="account_types" value={type} />
                  {type}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      </form>
    </div>
  );
}
